import _ from 'lodash';

/**
 *
 * @param {*} value
 * @returns Returns the shallow copy of the specified value.
 */
export const copy = (value) => _.clone(value);

/**
 *
 * @param {*} value
 * @description This method is like copy except that it recursively clones value. Use it when value has nested depths inside.
 * @returns Returns the deep cloned value.
 */
export const deepCopy = (value) => _.cloneDeep(value);

/**
 *
 * @param {string} [prefix]
 * @returns Returns the unique ID.
 */
export const uId = (prefix = null) => _.uniqueId(prefix);

/**
 *
 * @param {*} value
 * @param {*} other
 * @returns Returns true if the values are equivalent, else false.
 */
export const isItEqual = (value, other) => _.isEqual(value, other);

/**
 * @description Checks if value is empty.
 * A value is considered empty unless it’s an arguments object, array, string,
 * or jQuery-like collection with a length greater than 0 or an object with own enumerable properties.
 * @param {*} value
 * @returns Returns true if value is empty, else false.
 */
export const isItEmpty = (value) => _.isEmpty(value);

/**
 *
 * @param {object} object
 * @param {array<string> | string} path
 * @param {*} value
 * @description This method mutates the object!
 * @returns Returns the shallow copy of the object.
 */
export const setValue = (object, path, value) => _.set(object, path, value);

/**
 *
 * @description Gets the property value at path of object. If the resolved value is undefined the defaultValue is used in its place.
 * @param {object} object The object to query.
 * @param {array<string> | string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned if the resolved value is undefined. By default it's false.
 * @return Returns the resolved value.
 */
export const getValue = (object, path, defaultValue = false) => _.get(object, path, defaultValue);

/**
 *
 * @param {Object} object
 * @param {Array<String>} path
 * @returns Returns the new object without the specified keys in the path.
 */
export const exclude = (object, path) => _.omit(object, path);

/**
 *
 * @description This method removes all the occourencies specified in "arrayToRemove" from "baseArray".
 * It accepts a comparison criteria to handle the mutation.
 * N.B. This method mutates the given "baseArray"
 * @example pullFromArray([{x: 1, y: 'xo'}, {x: 2, y: 'xo'}, {x: 3, y: 'xo'}], [1,2], 'x'); // => [{x: 3, y: 'xo'}]
 * @param {array<T>} baseArray
 * @param {array<T>} arrayToRemove
 * @param {string} [criteria]
 * @returns Returns the array after mutation
 */
export const pullFromArray = (baseArray, arrayToRemove, criteria) => _.pullAllBy(baseArray, arrayToRemove, criteria);

/**
 *
 * @param {Object} object
 * @param {Array<String> | String} path
 * @returns Returns true if path exists, else false.
 */
export const isPresent = (object, path) => _.has(object, path);

/**
 * @description This method Checks if it is array and is not empty.
 * @param {*} value
 * @returns Returns true if value is array and it is empty, else false.
 */
export const isArrayEmpty = (array) => (!_.isArray(array) || (isItEmpty(array)));

/**
 * @description This method is used to create an array of numbers progressing from the given start value up to, but not including the end value.
 * @param {number} start // It is a number that specifies the start of the range. It is an optional value. The default value is 0.
 * @param {number} end // It is a number that specifies the end of the range.
 * @param {number} step // It is a number that specifies the amount that the value in the range is incremented or decremented. The default value is 1
 * @returns It returns an array with the given range of numbers.
 */
export const range = (start, end, step) => _.range(start, end, step);

/**
 * @description The method is used to create an array of unique values.
 * @param {array} array // This parameter holds the arrays to inspect.
 * @param {array} identity // This parameter holds the iteratee invoked per element.
 * @returns This method is used to return the new duplicate free array.
 */
export const uniqBy = (array, identity) => _.uniqBy(array, identity);

/**
 * @description The method is used to compare two arrays of objects by a specific key, the order and references of result values are determined by the first array.
 * @param {array} objArr1 // This parameter holds the first array to compare.
 * @param {array} objArr2 // This parameter holds the second array to compare.
 * @param {string} key // This parameter holds the key used as a comparator between the arrays passed as props.
 * @returns This method is used to return a new array with the elements that are present in both arrays.
 */
export const intersectionByKey = (objArr1, objArr2, key) => _.intersectionBy(objArr1, objArr2, key);

/**
 * @description The method is used to compare two arrays of objects by a specific key, the order and references of result values are determined by the first array.
 * @param {array} objArr1 // This parameter holds the first array to compare.
 * @param {array} objArr2 // This parameter holds the second array to compare.
 * @param {string} key // This parameter holds the key used as a comparator between the arrays passed as props.
 * @returns This method is used to return a new array with the elements that differ from both the arrays.
 */
export const differenceByKey = (objArr1, objArr2, key) => _.differenceBy(objArr1, objArr2, key);

/**
 *
 * @param {*} value
 * @returns Returns true if the passed value is "NaN", or Not A Number
 */
export const isNotNum = (value) => _.isNaN(value);

/**
 * @description The method takes two arrays and returns an array containing all the values in the first array that are not in the second array.
 * @param {array} arr1 // This parameter holds the first array to compare.
 * @param {array} arr2 // This parameter holds the second array to compare.
 * @returns returns an array containing all the values in the first array that are not in the second array.
 */
export const getDifference = (arr1, arr2) => _.difference(arr1, arr2);

/**
 * @description The method creates an array of unique values that are included in all given arrays
 * @param {array} arr1 // This parameter holds the first array to compare.
 * @param {array} arr2 // This parameter holds the second array to compare.
 * @returns  Returns the new array of intersecting values.
 */
export const getIntersection = (arr1, arr2) => _.intersection(arr1, arr2);

/**
 * @description The method sorts an array of objects given a specific key
 * @param {array} arr // This parameter holds the array to sort.
 * @param {key} key // This parameter holds the key to sort the array with.
 * @returns  Returns the new sorted array.
 */
export const sortBy = (arr, key) => _.sortBy(arr, key);
